// Festive decorative elements for headers and sections
interface FestiveDecorationProps {
  variant?: 'snowflakes' | 'gifts' | 'stars' | 'ornaments' | 'community';
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  className?: string;
}

export function FestiveDecoration({ 
  variant = 'snowflakes', 
  position = 'top-right',
  className = '' 
}: FestiveDecorationProps) {
  const positionClasses = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-right': 'bottom-0 right-0',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
  };

  if (variant === 'snowflakes') {
    return (
      <div className={`absolute ${positionClasses[position]} pointer-events-none ${className}`}>
        <svg width="120" height="120" viewBox="0 0 120 120" className="opacity-10">
          {/* Snowflake 1 */}
          <g transform="translate(30, 30)">
            <path d="M 0,-15 L 0,15 M -15,0 L 15,0 M -10,-10 L 10,10 M -10,10 L 10,-10" 
                  stroke="#2d5016" strokeWidth="2" fill="none" />
            <circle cx="0" cy="0" r="3" fill="#f4a460" />
          </g>
          
          {/* Snowflake 2 */}
          <g transform="translate(80, 80)">
            <path d="M 0,-12 L 0,12 M -12,0 L 12,0 M -8,-8 L 8,8 M -8,8 L 8,-8" 
                  stroke="#c41e3a" strokeWidth="2" fill="none" />
            <circle cx="0" cy="0" r="2.5" fill="#f4a460" />
          </g>
        </svg>
      </div>
    );
  }

  if (variant === 'gifts') {
    return (
      <div className={`absolute ${positionClasses[position]} pointer-events-none ${className}`}>
        <svg width="100" height="100" viewBox="0 0 100 100" className="opacity-10">
          {/* Gift box */}
          <rect x="20" y="35" width="30" height="30" fill="#2d5016" rx="2" />
          <rect x="20" y="30" width="30" height="8" fill="#c41e3a" rx="1" />
          <rect x="33" y="30" width="4" height="35" fill="#f4a460" />
          
          {/* Small gift */}
          <rect x="55" y="50" width="20" height="20" fill="#c41e3a" rx="1" />
          <rect x="63" y="50" width="4" height="20" fill="#f4a460" />
        </svg>
      </div>
    );
  }

  if (variant === 'stars') {
    return (
      <div className={`absolute ${positionClasses[position]} pointer-events-none ${className}`}>
        <svg width="100" height="100" viewBox="0 0 100 100" className="opacity-10">
          {/* Star 1 */}
          <path d="M 25,15 L 27,22 L 34,22 L 28,27 L 31,34 L 25,29 L 19,34 L 22,27 L 16,22 L 23,22 Z" 
                fill="#f4a460" />
          
          {/* Star 2 */}
          <path d="M 70,50 L 72,57 L 79,57 L 73,62 L 76,69 L 70,64 L 64,69 L 67,62 L 61,57 L 68,57 Z" 
                fill="#2d5016" />
        </svg>
      </div>
    );
  }

  if (variant === 'community') {
    return (
      <div className={`absolute ${positionClasses[position]} pointer-events-none ${className}`}>
        <svg width="100" height="100" viewBox="0 0 100 100" className="opacity-15">
          <defs>
            <linearGradient id="community-gradient-decoration" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#f4a460" />
              <stop offset="50%" stopColor="#c41e3a" />
              <stop offset="100%" stopColor="#2d5016" />
            </linearGradient>
          </defs>
          <g transform="translate(50, 50)">
            {/* Small version of the logo icon - people in circle */}
            {[0, 90, 180, 270].map((angle) => (
              <g key={angle} transform={`rotate(${angle}, 0, 0)`}>
                <circle cx="0" cy="-22" r="3" fill="url(#community-gradient-decoration)" />
                <path d="M 0,-19 L -3,-12 L 0,-14 L 3,-12 L 0,-19 Z" fill="url(#community-gradient-decoration)" />
                <path d="M 0,-12 L -1.5,-6" stroke="url(#community-gradient-decoration)" strokeWidth="2" />
                <path d="M 0,-12 L 1.5,-6" stroke="url(#community-gradient-decoration)" strokeWidth="2" />
              </g>
            ))}
            <circle cx="0" cy="0" r="3" fill="url(#community-gradient-decoration)" />
          </g>
        </svg>
      </div>
    );
  }

  // ornaments variant
  return (
    <div className={`absolute ${positionClasses[position]} pointer-events-none ${className}`}>
      <svg width="100" height="120" viewBox="0 0 100 120" className="opacity-10">
        {/* Ornament 1 */}
        <circle cx="30" cy="40" r="15" fill="#c41e3a" />
        <rect x="27" y="25" width="6" height="5" fill="#2d5016" rx="1" />
        <path d="M 30,25 L 30,15" stroke="#f4a460" strokeWidth="1.5" />
        
        {/* Ornament 2 */}
        <circle cx="60" cy="80" r="12" fill="#2d5016" />
        <rect x="57.5" y="68" width="5" height="4" fill="#c41e3a" rx="1" />
        <path d="M 60,68 L 60,60" stroke="#f4a460" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

// Festive divider/separator
export function FestiveDivider({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-4 ${className}`}>
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/20 to-primary/20" />
      <svg width="32" height="32" viewBox="0 0 32 32">
        <defs>
          <linearGradient id="divider-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f4a460" />
            <stop offset="50%" stopColor="#c41e3a" />
            <stop offset="100%" stopColor="#2d5016" />
          </linearGradient>
        </defs>
        <g transform="translate(16, 16)">
          {/* Simplified 4-person circle */}
          {[0, 90, 180, 270].map((angle, i) => (
            <g key={i} transform={`rotate(${angle}, 0, 0)`}>
              <circle cx="0" cy="-10" r="1.5" fill="url(#divider-gradient)" />
              <path d="M 0,-8.5 L -1.5,-5 L 0,-6 L 1.5,-5 L 0,-8.5 Z" fill="url(#divider-gradient)" />
            </g>
          ))}
          <circle cx="0" cy="0" r="2" fill="url(#divider-gradient)" />
        </g>
      </svg>
      <div className="h-px flex-1 bg-gradient-to-l from-transparent via-primary/20 to-primary/20" />
    </div>
  );
}

// Festive corner decoration for cards
export function FestiveCorner({ 
  corner = 'top-right',
  className = '' 
}: { 
  corner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  className?: string;
}) {
  const rotations = {
    'top-right': 'rotate-0',
    'bottom-right': 'rotate-90',
    'bottom-left': 'rotate-180',
    'top-left': 'rotate-270'
  };

  const positions = {
    'top-right': 'top-0 right-0',
    'bottom-right': 'bottom-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'top-left': 'top-0 left-0'
  };

  return (
    <div className={`absolute ${positions[corner]} ${className}`}>
      <svg width="40" height="40" viewBox="0 0 40 40" className={`${rotations[corner]} opacity-20`}>
        <path
          d="M 0,0 L 40,0 L 40,5 Q 20,10 5,20 L 0,40 Z"
          fill="url(#festive-gradient)"
        />
        <defs>
          <linearGradient id="festive-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2d5016" />
            <stop offset="50%" stopColor="#f4a460" />
            <stop offset="100%" stopColor="#c41e3a" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}
